// AdminPage.js
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { isAdminUser } from "../utils/admin";
import "./AdminPage.css";
import AdminRoughnessCalibration from "./AdminRoughnessCalibration";

const ADMIN_USERS_CACHE_KEY = "adminUsersCache";
const ADMIN_CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

function getCachedUsers() {
  try {
    const raw = sessionStorage.getItem(ADMIN_USERS_CACHE_KEY);
    if (!raw) return null;
    const { users, totalRuns, totalYouTubeRuns, fetchedAt } = JSON.parse(raw);
    if (Date.now() - fetchedAt > ADMIN_CACHE_TTL_MS) return null;
    return { users, totalRuns, totalYouTubeRuns };
  } catch {
    return null;
  }
}

function setCachedUsers(users, totalRuns, totalYouTubeRuns) {
  try {
    sessionStorage.setItem(
      ADMIN_USERS_CACHE_KEY,
      JSON.stringify({
        users,
        totalRuns,
        totalYouTubeRuns,
        fetchedAt: Date.now(),
      })
    );
  } catch {
    // ignore
  }
}

const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: "username", direction: "asc" });
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("users");
  const [totalRuns, setTotalRuns] = useState(0);
  const [totalYouTubeRuns, setTotalYouTubeRuns] = useState(0);

  useEffect(() => {
    if (!isAdminUser(user)) {
      navigate("/");
      return;
    }

    const fetchUsers = async () => {
      const cached = getCachedUsers();
      if (cached) {
        setUsers(cached.users);
        setTotalRuns(cached.totalRuns);
        setTotalYouTubeRuns(cached.totalYouTubeRuns);
        setLoading(false);
      } else {
        setLoading(true);
      }

      try {
        const usersRef = collection(db, "users");
        const usersSnapshot = await getDocs(usersRef);
        const sortedUserDocs = [...usersSnapshot.docs].sort((a, b) =>
          b.id.localeCompare(a.id)
        );

        const runsRef = collection(db, "runs");
        const runsSnapshot = await getDocs(runsRef);
        const runCountByUser = {};
        let totalRunsCount = 0;
        let totalYouTubeRunsCount = 0;
        runsSnapshot.docs.forEach((docSnap) => {
          const d = docSnap.data();
          const uid = d.userId || null;
          if (!uid) return;
          if (!runCountByUser[uid]) runCountByUser[uid] = { runs: 0, youtube: 0 };
          runCountByUser[uid].runs += 1;
          if (d.youtubeUrl) runCountByUser[uid].youtube += 1;
          totalRunsCount += 1;
          if (d.youtubeUrl) totalYouTubeRunsCount += 1;
        });

        const usersData = sortedUserDocs.map((docSnap) => {
          const userData = docSnap.data();
          const counts = runCountByUser[docSnap.id] || { runs: 0, youtube: 0 };
          return {
            id: docSnap.id,
            username: userData.username || "No username",
            email: userData.email || "No email",
            credits: userData.credits ?? 0,
            runs: counts.runs,
            youtubeRuns: counts.youtube,
            origin: userData.origin || "Unknown",
          };
        });

        setUsers(usersData);
        setTotalRuns(totalRunsCount);
        setTotalYouTubeRuns(totalYouTubeRunsCount);
        setCachedUsers(usersData, totalRunsCount, totalYouTubeRunsCount);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [navigate, user]);

  // Sorting function
  const sortUsers = (key) => {
    setSortConfig((prev) => {
      const nextDirection = prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction: nextDirection };
    });
  };

  const visibleUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = q
      ? users.filter((u) => {
          return (
            String(u.username ?? "").toLowerCase().includes(q) ||
            String(u.email ?? "").toLowerCase().includes(q) ||
            String(u.origin ?? "").toLowerCase().includes(q) ||
            String(u.id ?? "").toLowerCase().includes(q)
          );
        })
      : users;

    const { key, direction } = sortConfig;
    const dir = direction === "asc" ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
      const av = a?.[key];
      const bv = b?.[key];

      if (key === "id") return dir * String(av ?? "").localeCompare(String(bv ?? ""));
      if (typeof av === "number" && typeof bv === "number") return dir * (av - bv);

      return dir * String(av ?? "").localeCompare(String(bv ?? ""));
    });

    return sorted;
  }, [searchQuery, sortConfig, users]);

  const sortIndicator = (key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  return (
    <div className="admin-page-container">
      <div className="admin-page-main">
        <h1>Admin Panel</h1>

        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <button
            type="button"
            onClick={() => setActiveTab("users")}
            aria-pressed={activeTab === "users"}
          >
            Users
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("roughness")}
            aria-pressed={activeTab === "roughness"}
          >
            Roughness calibration
          </button>
        </div>

        {activeTab === "roughness" && <AdminRoughnessCalibration />}

        {activeTab === "users" && (
          <>
            <div className="admin-page-toolbar">
              <h2>Users ({visibleUsers.length}{searchQuery.trim() ? ` / ${users.length}` : ""})</h2>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users (username, email, origin, id)"
                aria-label="Search users"
                style={{ maxWidth: 360, width: "100%" }}
              />
            </div>

            {loading ? (
              <p className="admin-page-section">
                <span className="admin-loading-spinner" aria-hidden />
                Loading users...
              </p>
            ) : visibleUsers.length > 0 ? (
              <div className="admin-page-section">
              <table className="run-table">
                <thead>
                  <tr>
                    <th onClick={() => sortUsers("username")}>
                      Username{sortIndicator("username")}
                    </th>
                    <th onClick={() => sortUsers("email")}>
                      Email{sortIndicator("email")}
                    </th>
                    <th onClick={() => sortUsers("credits")}>
                      Credits{sortIndicator("credits")}
                    </th>
                    <th onClick={() => sortUsers("runs")}>
                      Runs Created ({totalRuns}){sortIndicator("runs")}
                    </th>
                    <th onClick={() => sortUsers("youtubeRuns")}>
                      Runs on YouTube ({totalYouTubeRuns}){sortIndicator("youtubeRuns")}
                    </th>
                    <th onClick={() => sortUsers("origin")}>
                      Origin{sortIndicator("origin")}
                    </th>
                    <th onClick={() => sortUsers("id")}>
                      User ID{sortIndicator("id")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleUsers.map((u) => (
                    <tr key={u.id}>
                      <td>{u.username}</td>
                      <td>{u.email}</td>
                      <td>{u.credits}</td>
                      <td>{u.runs}</td>
                      <td>{u.youtubeRuns}</td>
                      <td>{u.origin}</td>
                      <td>{u.id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            ) : (
              <p className="admin-page-section">No users found.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPage;